import {
  StakeProgram,
  SystemProgram,
  SYSVAR_CLOCK_PUBKEY,
  SYSVAR_RENT_PUBKEY,
  SYSVAR_STAKE_HISTORY_PUBKEY
} from "@solana/web3.js";
import {
  findBSolTokenAccountAuthority,
  findEpochReportAccount,
  findGSolMintAuthority,
  findMSolTokenAccountAuthority,
  findOrderUnstakeTicketAccount,
  getValidatorIndex
} from "./util";
import {
  MarinadeUtils
} from "@sunrisestake/marinade-ts-sdk";
import { utils } from "@coral-xyz/anchor";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import BN from "bn.js";
import { STAKE_POOL_PROGRAM_ID } from "./constants";
const deposit = async (config, program, marinade, marinadeState, stateAddress, staker, recipientGsolTokenAccount, lamports) => {
  const sunriseStakeState = await program.account.state.fetch(stateAddress);
  const marinadeProgram = marinade.marinadeFinanceProgram.programAddress;
  const [gsolMintAuthority] = findGSolMintAuthority(config);
  const msolTokenAccountAuthority = findMSolTokenAccountAuthority(config)[0];
  const msolAssociatedTokenAccountAddress = await utils.token.associatedAddress(
    {
      mint: marinadeState.mSolMintAddress,
      owner: msolTokenAccountAuthority
    }
  );
  const liqPoolAssociatedTokenAccountAddress = await utils.token.associatedAddress({
    mint: marinadeState.lpMint.address,
    owner: msolTokenAccountAuthority
  });
  const accounts = {
    state: stateAddress,
    marinadeState: marinadeState.marinadeStateAddress,
    gsolMint: sunriseStakeState.gsolMint,
    gsolMintAuthority,
    msolMint: marinadeState.mSolMint.address,
    liqPoolMint: marinadeState.lpMint.address,
    liqPoolSolLegPda: await marinadeState.solLeg(),
    liqPoolMsolLeg: marinadeState.mSolLeg,
    liqPoolMsolLegAuthority: await marinadeState.mSolLegAuthority(),
    liqPoolMintAuthority: await marinadeState.lpMintAuthority(),
    reservePda: await marinadeState.reserveAddress(),
    transferFrom: staker,
    mintMsolTo: msolAssociatedTokenAccountAddress,
    mintLiqPoolTo: liqPoolAssociatedTokenAccountAddress,
    mintGsolTo: recipientGsolTokenAccount,
    msolMintAuthority: await marinadeState.mSolMintAuthority(),
    msolTokenAccountAuthority,
    systemProgram: SystemProgram.programId,
    tokenProgram: TOKEN_PROGRAM_ID,
    marinadeProgram
  };
  return program.methods.deposit(lamports).accounts(accounts).transaction();
};
const depositStakeAccount = async (config, program, marinade, marinadeState, staker, stakeAccountAddress, stakerGsolTokenAccount) => {
  const stateAddress = config.stateAddress;
  const sunriseStakeState = await program.account.state.fetch(stateAddress);
  const marinadeProgram = marinade.marinadeFinanceProgram.programAddress;
  const [gsolMintAuthority] = findGSolMintAuthority(config);
  const msolTokenAccountAuthority = findMSolTokenAccountAuthority(config)[0];
  const msolAssociatedTokenAccountAddress = await utils.token.associatedAddress(
    {
      mint: marinadeState.mSolMintAddress,
      owner: msolTokenAccountAuthority
    }
  );
  const stakeAccountInfo = await MarinadeUtils.getParsedStakeAccountInfo(
    marinade.provider,
    stakeAccountAddress
  );
  const voterAddress = stakeAccountInfo.voterAddress;
  if (!voterAddress) {
    throw new Error("The stake account must be delegated");
  }
  console.log("voterAddress: ", voterAddress);
  console.log(
    "validator list: ",
    marinadeState.state.validatorSystem.validatorList.account.toString()
  );
  console.log(
    "stake list: ",
    marinadeState.state.stakeSystem.stakeList.account.toString()
  );
  console.log(
    "duplication flag: ",
    (await marinadeState.validatorDuplicationFlag(voterAddress)).toString()
  );
  const validatorSystem = marinadeState.state.validatorSystem;
  const stakeSystem = marinadeState.state.stakeSystem;
  const accounts = {
    state: stateAddress,
    marinadeState: marinadeState.marinadeStateAddress,
    gsolMint: sunriseStakeState.gsolMint,
    gsolMintAuthority,
    validatorList: validatorSystem.validatorList.account,
    stakeList: stakeSystem.stakeList.account,
    stakeAccount: stakeAccountAddress,
    duplicationFlag: await marinadeState.validatorDuplicationFlag(voterAddress),
    stakeAuthority: staker,
    msolMint: marinadeState.mSolMint.address,
    mintMsolTo: msolAssociatedTokenAccountAddress,
    mintGsolTo: stakerGsolTokenAccount,
    msolMintAuthority: await marinadeState.mSolMintAuthority(),
    msolTokenAccountAuthority,
    clock: SYSVAR_CLOCK_PUBKEY,
    rent: SYSVAR_RENT_PUBKEY,
    stakeProgram: StakeProgram.programId,
    systemProgram: SystemProgram.programId,
    tokenProgram: TOKEN_PROGRAM_ID,
    marinadeProgram
  };
  console.log("accounts: ", accounts);
  console.log("Getting validator index");
  const validatorIndex = await getValidatorIndex(marinadeState, voterAddress);
  return program.methods.depositStakeAccount(validatorIndex).accounts(accounts).transaction();
};
const getEpochReportAccount = async (config, program) => {
  const [address, bump] = findEpochReportAccount(config);
  const account = await program.account.epochReportAccount.fetchNullable(
    address
  );
  return {
    address,
    bump,
    account
  };
};
const liquidUnstake = async (config, blaze, marinade, marinadeState, program, stateAddress, staker, stakerGsolTokenAccount, lamports) => {
  const sunriseStakeState = await program.account.state.fetch(stateAddress);
  const marinadeProgram = marinade.marinadeFinanceProgram.programAddress;
  const [gsolMintAuthority] = findGSolMintAuthority(config);
  const msolTokenAccountAuthority = findMSolTokenAccountAuthority(config)[0];
  const msolAssociatedTokenAccountAddress = await utils.token.associatedAddress(
    {
      mint: marinadeState.mSolMintAddress,
      owner: msolTokenAccountAuthority
    }
  );
  const liqPoolAssociatedTokenAccountAddress = await utils.token.associatedAddress({
    mint: marinadeState.lpMint.address,
    owner: msolTokenAccountAuthority
  });
  const bsolTokenAccountAuthority = findBSolTokenAccountAuthority(config)[0];
  const bsolAssociatedTokenAddress = await utils.token.associatedAddress({
    mint: blaze.bsolMint,
    owner: bsolTokenAccountAuthority
  });
  const accounts = {
    state: stateAddress,
    marinadeState: marinadeState.marinadeStateAddress,
    msolMint: marinadeState.mSolMint.address,
    liqPoolMint: marinadeState.lpMint.address,
    gsolMint: sunriseStakeState.gsolMint,
    gsolMintAuthority,
    liqPoolSolLegPda: await marinadeState.solLeg(),
    liqPoolMsolLeg: marinadeState.mSolLeg,
    liqPoolMsolLegAuthority: await marinadeState.mSolLegAuthority(),
    treasuryMsolAccount: marinadeState.treasuryMsolAccount,
    getMsolFrom: msolAssociatedTokenAccountAddress,
    getMsolFromAuthority: msolTokenAccountAuthority,
    getLiqPoolTokenFrom: liqPoolAssociatedTokenAccountAddress,
    gsolTokenAccount: stakerGsolTokenAccount,
    gsolTokenAccountAuthority: staker,
    systemProgram: SystemProgram.programId,
    tokenProgram: TOKEN_PROGRAM_ID,
    rent: SYSVAR_RENT_PUBKEY,
    marinadeProgram,
    bsolTokenAccount: bsolAssociatedTokenAddress,
    bsolAccountAuthority: bsolTokenAccountAuthority,
    blazeStakePool: blaze.pool,
    stakePoolWithdrawAuthority: blaze.withdrawAuthority,
    reserveStakeAccount: blaze.reserveAccount,
    managerFeeAccount: blaze.feesDepot,
    bsolMint: blaze.bsolMint,
    sysvarStakeHistory: SYSVAR_STAKE_HISTORY_PUBKEY,
    stakePoolProgram: STAKE_POOL_PROGRAM_ID,
    nativeStakeProgram: StakeProgram.programId,
    clock: SYSVAR_CLOCK_PUBKEY
  };
  return program.methods.liquidUnstake(lamports).accounts(accounts).transaction();
};
const triggerRebalance = async (config, marinade, marinadeState, program, stateAddress, payer) => {
  const sunriseStakeState = await program.account.state.fetch(stateAddress);
  const marinadeProgram = marinade.marinadeFinanceProgram.programAddress;
  const msolTokenAccountAuthority = findMSolTokenAccountAuthority(config)[0];
  const msolAssociatedTokenAccountAddress = await utils.token.associatedAddress(
    {
      mint: marinadeState.mSolMintAddress,
      owner: msolTokenAccountAuthority
    }
  );
  const liqPoolAssociatedTokenAccountAddress = await utils.token.associatedAddress({
    mint: marinadeState.lpMint.address,
    owner: msolTokenAccountAuthority
  });
  const epochInfo = await program.provider.connection.getEpochInfo();
  const { account: epochReportAccount, address: epochReportAccountAddress } = await getEpochReportAccount(config, program);
  if (!epochReportAccount)
    throw new Error("No epoch report account found");
  const index = epochReportAccount.tickets.toNumber() ?? 0;
  const [orderUnstakeTicketAccount, orderUnstakeTicketAccountBump] = findOrderUnstakeTicketAccount(
    config,
    BigInt(epochInfo.epoch),
    BigInt(index)
  );
  const accounts = {
    state: stateAddress,
    payer,
    marinadeState: marinadeState.marinadeStateAddress,
    gsolMint: sunriseStakeState.gsolMint,
    msolMint: marinadeState.mSolMint.address,
    liqPoolMint: marinadeState.lpMint.address,
    liqPoolSolLegPda: await marinadeState.solLeg(),
    liqPoolMsolLeg: marinadeState.mSolLeg,
    liqPoolMsolLegAuthority: await marinadeState.mSolLegAuthority(),
    liqPoolMintAuthority: await marinadeState.lpMintAuthority(),
    liqPoolTokenAccount: liqPoolAssociatedTokenAccountAddress,
    reservePda: await marinadeState.reserveAddress(),
    treasuryMsolAccount: marinadeState.treasuryMsolAccount,
    getMsolFrom: msolAssociatedTokenAccountAddress,
    getMsolFromAuthority: msolTokenAccountAuthority,
    orderUnstakeTicketAccount,
    epochReportAccount: epochReportAccountAddress,
    systemProgram: SystemProgram.programId,
    tokenProgram: TOKEN_PROGRAM_ID,
    clock: SYSVAR_CLOCK_PUBKEY,
    rent: SYSVAR_RENT_PUBKEY,
    marinadeProgram
  };
  const instruction = await program.methods.triggerPoolRebalance(
    new BN(epochInfo.epoch),
    new BN(index),
    orderUnstakeTicketAccountBump
  ).accounts(accounts).instruction();
  return {
    instruction,
    orderUnstakeTicketAccount,
    epochReportAccount: epochReportAccountAddress
  };
};
export {
  deposit,
  depositStakeAccount,
  getEpochReportAccount,
  liquidUnstake,
  triggerRebalance
};
